import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../helpers/hooks';
import { selectLanguages } from '../../languages/slice/selectors';
import { selectProjectCreating } from '../slice/selectors';
import { useProjectsSlice } from '../slice';

interface CreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateProjectDialog({
  open,
  onClose,
}: CreateProjectDialogProps) {
  const dispatch = useAppDispatch();
  const { actions } = useProjectsSlice();
  const languages = useAppSelector(selectLanguages);
  const creating = useAppSelector(selectProjectCreating);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);

  const handleSubmit = () => {
    dispatch(
      actions.createRequest({
        name,
        description: description || undefined,
        sourceLanguage,
        targetLanguages,
      }),
    );
    onClose();
    setName('');
    setDescription('');
    setTargetLanguages([]);
  };

  const targetOptions = languages.filter((l) => l.code !== sourceLanguage);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create project</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label="Project name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Source language
            </Typography>
            <Select
              value={sourceLanguage}
              onChange={(e) => setSourceLanguage(e.target.value)}
              fullWidth
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Target languages
            </Typography>
            <Select
              multiple
              value={targetLanguages}
              onChange={(e) =>
                setTargetLanguages(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value,
                )
              }
              input={<OutlinedInput />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              fullWidth
            >
              {targetOptions.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name || creating}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
